import React, { useState } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import AnimatedGrid from './AnimatedGrid';
import ThreeStepGuide from './ThreeStepGuide';
import VideoGallery from './VideoGallery';
import GetStartedTooltip from './GetStartedTooltip';

interface HeroProps {
  isMember?: boolean;
  videos?: Array<{
    id: string;
    title: string;
    src?: string;
    youtubeId?: string;
    thumbnail?: string;
    description?: string;
  }>;
  showVideoGallery?: boolean;
  tooltipPosition?: 'bottom-right' | 'top-right';
}

const Hero: React.FC<HeroProps> = ({ 
  isMember = false, 
  videos = [],
  showVideoGallery = false,
  tooltipPosition = 'bottom-right'
}) => {
  // Function to scroll to community section
  const handleScrollToCommunity = () => {
    const communitySection = document.getElementById('community');
    if (communitySection) {
      communitySection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleGetStarted = () => {
    // Navigate to app with KYC modal parameter - app.bornfree.io handles ?kyc=true to open KYC modal
    // Same pattern as "Get Member ID" button in ThreeStepGuide modal
    window.open('https://app.bornfree.io/?kyc=true', '_blank');
  };

  return (
    <section id="home" className="relative flex flex-col items-center justify-start text-center overflow-hidden py-12 md:py-16 min-h-screen" style={{ paddingTop: '116px' }}>
      <AnimatedGrid />
      
      {/* Decorative elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-cyan-400/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 -left-20 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>
      
      {/* Content container - Title moved up with more top space */}
      <div className="container mx-auto px-6 relative z-10 mb-6 mt-16 md:mt-20">
        <div className="flex flex-col items-center max-w-4xl mx-auto">
          {/* Text content - Compact */}
          <div className="animate-fade-in w-full mb-6">
            <h1 className="font-extrabold mb-2 text-3xl md:text-4xl lg:text-5xl" style={{ color: '#36a9f7' }}>
              FREEDOM REIMAGINED
            </h1>
            
            <p className="text-gray-300 font-normal text-xl md:text-2xl mb-6 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              Strength In Numbers
            </p>
            
            <div className="mb-3 animate-slide-up" style={{ animationDelay: '0.4s' }}>
              <span 
                className="font-bold tracking-wider text-2xl md:text-3xl lg:text-4xl"
                style={{ color: '#7dd3fc' }}
              >
                Digital Keys to Real Experiences
              </span>
            </div>
            
            <p className="text-gray-200 text-base md:text-lg mb-2 max-w-2xl mx-auto animate-slide-up" style={{ animationDelay: '0.6s' }}>
              Places, People, Events, Activities, Media, and more…
            </p>
          </div>
        </div>
      </div>

      {/* 3-Step Guide */}
      <div className="container mx-auto px-6 relative z-10 mb-12 mt-16">
        <ThreeStepGuide variant="default" />
      </div>

      {/* Video Gallery Section */}
      {showVideoGallery && videos.length > 0 && (
        <div className="container mx-auto px-6 relative mt-8" style={{ zIndex: 1 }}>
          <VideoGallery videos={videos} autoPlay={true} showThumbnails={true} />
        </div>
      )}

      {/* Placeholder for single main video (if no gallery) */}
      {!showVideoGallery && videos.length > 0 && (
        <div className="container mx-auto px-6 relative z-10 mt-8 max-w-4xl">
          <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden shadow-2xl">
            <video
              src={videos[0].src}
              className="w-full h-full object-contain"
              controls
              playsInline
            />
          </div>
        </div>
      )}

      {/* Get Started Tooltip (glowing i icon) */}
      <GetStartedTooltip 
        isMember={isMember} 
        onGetStarted={handleGetStarted}
        position={tooltipPosition}
      />
    </section>
  );
};

export default Hero;
